import React, { useState, useEffect } from 'react';
import Navbar from '../../../../components/Navbar';
import { useNavigate, useParams } from 'react-router-dom';
import { createAllPhaseMenuGroups } from '../../../../utils/createAllPhaseMenuGroups';
import axiosInstance from '../../../../utils/axiosInstance';
import config from '../../../../config.json';
import Graph from "react-vis-network-graph";
import { genEdge, genNode } from "../../../../utils/ideaTool";
import {
  Typography,
  Box,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Divider,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Switch,
  Paper,
  CircularProgress
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Book as BookIcon,
  Apps as AppsIcon,
  School as SchoolIcon,
  People as PeopleIcon,
  Home as HomeIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Send as SendIcon,
  Schedule as ScheduleIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Lightbulb as LightbulbIcon,
  Forum as ForumIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  FilterList as FilterListIcon,
  Sort as SortIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Launch as LaunchIcon
} from '@mui/icons-material';
import Forumm from '../../../../components/Forumm';
import io from "socket.io-client";
import url from "../../../../url.json";

export default function ClassApplicationIdeaWall() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [viewMode, setViewMode] = useState('multiple'); // 預設多組觀察模式
  const [ideas, setIdeas] = useState({});
  const [groups, setGroups] = useState([]);
  const [wallNodes, setWallNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [ws, setWs] = useState(null);
  const [allNodes, setAllNodes] = useState([]); // 用於 WebSocket 即時更新
  const [graphKey, setGraphKey] = useState(0); // 用於強制重新渲染圖形
  const [highlightedNodes, setHighlightedNodes] = useState(new Set()); // 追蹤需要高亮的新節點
  const [courseDispatch, setCourseDispatch] = useState(null); // 課程派發資訊

  // 處理多組選擇
  const handleGroupToggle = (groupId) => {
    if (viewMode === 'single') {
      // 單組模式：只能選擇一個
      setSelectedGroups([groupId]);
    } else {
      // 多組模式：可以多選
      setSelectedGroups(prev => {
        if (prev.includes(groupId)) {
          return prev.filter(id => id !== groupId);
        } else {
          return [...prev, groupId];
        }
      });
    }
  };

  // 獲取所有選中組的想法
  const getAllSelectedIdeas = () => {
    const allIdeas = [];
    selectedGroups.forEach(groupId => {
      const groupIdeas = ideas[groupId] || [];
      const groupName = groups.find(g => g.id === groupId)?.name || `小組${groupId}`;
      allIdeas.push(...groupIdeas.map(idea => ({ ...idea, groupId, groupName })));
    });
    return allIdeas;
  };

  const currentIdeas = viewMode === 'single' ? (ideas[selectedGroups[0]] || []) : getAllSelectedIdeas();

  // 調試日誌：檢查 currentIdeas 的計算
  console.log('🔍 [Teacher IdeaWall] currentIdeas 計算:');
  console.log('  - viewMode:', viewMode);
  console.log('  - selectedGroups:', selectedGroups);
  console.log('  - ideas keys:', Object.keys(ideas));
  console.log('  - currentIdeas length:', currentIdeas.length);
  console.log('  - currentIdeas:', currentIdeas.map(i => ({ id: i.id, title: i.title, groupId: i.groupId })));

  // 顏色映射（與 ideaTool.js 一致）
  const colorMap = {
    reply: { border: '#9E9E9E', background: '#FFFFFF' },      // 回覆 - 白色
    idea: { border: '#FFFFCC', background: '#FFFFCC' },       // 想法 - 黃色
    info: { border: '#CCFFCC', background: '#CCFFCC' }, // 資訊 - 綠色
    question: { border: '#CCCCFF', background: '#CCCCFF' },   // 提問 - 藍色
    experiment: { border: '#FFDBDB', background: '#FFDBDB' }, // 實驗 - 紅色
    record: { border: '#B9DCF4', background: '#B9DCF4' }      // 記錄 - 青色
  };

  // 獲取節點類型對應的 emoji
  const getNodeEmoji = (nodeType) => {
    const emojiMap = {
      'idea': '💡',
      'question': '❓',
      'experiment': '🧪',
      'record': '📄',
      'info': '🔍',
      'resource': '🔍',
      'task': '📋',
      'reply': '💬'
    };
    return emojiMap[nodeType] || '📝';
  };

  // 格式化時間戳
  const formatTimestamp = (timestamp) => {
    return new Intl.DateTimeFormat('zh-TW', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date(timestamp));
  };

  // 智能位置生成器 - 考慮節點關聯性
  const generateSmartPositions = (ideasList) => {
    const positions = {};
    const groups = new Map(); // 按小組分組
    const discussions = new Map(); // 按討論群組分組
    
    // 1. 按小組分組
    ideasList.forEach(idea => {
      if (!groups.has(idea.groupId)) {
        groups.set(idea.groupId, []);
      }
      groups.get(idea.groupId).push(idea);
    });
    
    // 2. 為每個小組分配一個區域，改善擁擠問題
    const groupAreas = new Map();
    let areaIndex = 0;
    groups.forEach((groupIdeas, groupId) => {
      // 根據節點數量動態調整區域大小
      const nodeCount = groupIdeas.length;
      const baseWidth = Math.max(300, Math.min(500, nodeCount * 80 + 200));
      const baseHeight = Math.max(300, Math.min(500, nodeCount * 80 + 200));
      
      // 使用更緊密的網格佈局，減少重疊
      const cols = Math.ceil(Math.sqrt(groups.size));
      const rows = Math.ceil(groups.size / cols);
      
      const areaX = (areaIndex % cols) * 450 + 60; // 增加間距
      const areaY = Math.floor(areaIndex / cols) * 450 + 60; // 增加間距
      
      groupAreas.set(groupId, { 
        x: areaX, 
        y: areaY, 
        width: baseWidth, 
        height: baseHeight 
      });
      areaIndex++;
    });
    
    // 3. 為每個節點生成智能位置，避免重疊
    ideasList.forEach(idea => {
      const groupArea = groupAreas.get(idea.groupId);
      if (groupArea) {
        // 檢查是否有其他節點指向這個節點（這個節點是回覆節點）
        const parentIdea = ideasList.find(parent => 
          parent.relatedTo && parent.relatedTo.includes(idea.id)
        );
        
        if (parentIdea && positions[parentIdea.id]) {
          // 這是回覆節點，圍繞原節點（父節點）分佈
          const angle = Math.random() * 2 * Math.PI;
          const distance = 100 + Math.random() * 80; // 增加距離避免重疊
          positions[idea.id] = {
            x: positions[parentIdea.id].x + Math.cos(angle) * distance,
            y: positions[parentIdea.id].y + Math.sin(angle) * distance
          };
        } else {
          // 這是原始節點或沒有父節點的節點，使用網格佈局避免重疊
          const groupIdeas = groups.get(idea.groupId) || [];
          const nodeIndex = groupIdeas.indexOf(idea);
          
          // 在組內使用網格佈局
          const colsPerGroup = Math.ceil(Math.sqrt(groupIdeas.length));
          const row = Math.floor(nodeIndex / colsPerGroup);
          const col = nodeIndex % colsPerGroup;
          
          const spacing = 120; // 節點間距
          const baseX = groupArea.x + col * spacing;
          const baseY = groupArea.y + row * spacing;
          
          // 添加一些隨機偏移，但保持網格結構
          const offsetX = (Math.random() - 0.5) * 40;
          const offsetY = (Math.random() - 0.5) * 40;
          
          positions[idea.id] = { 
            x: baseX + offsetX, 
            y: baseY + offsetY 
          };
        }
      } else {
        // 如果沒有小組信息，使用全螢幕隨機分佈
        positions[idea.id] = {
          x: 100 + Math.random() * 800,
          y: 100 + Math.random() * 600
        };
      }
    });
    
    return positions;
  };

  // 將想法轉換為 vis-network 格式（使用智能位置策略）
  const convertIdeasToGraph = (ideasList) => {
    const positions = generateSmartPositions(ideasList);
    
    const nodes = ideasList.map(idea => {
      const color = colorMap[idea.tags] || { border: '#CCCCCC', background: '#EEEEEE' };
      const position = positions[idea.id] || { x: 100, y: 100 };
      const nodeIdStr = String(idea.id);
      const isHighlighted = highlightedNodes.has(nodeIdStr);
      
      return {
        id: idea.id,
        label: `${idea.emoji}\n\n${idea.title}\n\n${idea.author}\n${idea.time}`,
        group: idea.tags,
        title: idea.content || idea.title,
        color: isHighlighted ? {
          border: '#fff0a3', // 更溫和的綠色邊框
          background: color.background,
          highlight: {
            border: '#fff0a3',
            background: color.background
          }
        } : color,
        borderWidth: isHighlighted ? 2 : 1, // 高亮時邊框稍微加粗
        borderWidthSelected: isHighlighted ? 2.5 : 2,
        x: position.x,
        y: position.y,
      };
    });

    const edges = [];
    ideasList.forEach(idea => {
      if (idea.relatedTo && idea.relatedTo.length > 0) {
        idea.relatedTo.forEach(relatedId => {
          edges.push({
            id: `${idea.id}-${relatedId}`,
            from: idea.id,
            to: relatedId,
            arrows: 'to'
          });
        });
      }
    });
    
    console.log('[Teacher] 轉換的節點數:', nodes.length, '邊緣數:', edges.length);
    console.log('[Teacher] 有 relatedTo 的節點:', ideasList.filter(i => i.relatedTo && i.relatedTo.length > 0).map(i => ({
      id: i.id,
      title: i.title,
      relatedTo: i.relatedTo
    })));

    return { nodes, edges };
  };

  const graph = convertIdeasToGraph(currentIdeas);

  // 圖形事件處理 - 老師觀察模式不需要節點互動
  const events = {
    // 老師在觀察小組時不需要點擊節點功能
  };

  // vis-network 選項
  const options = {
    layout: {
      randomSeed: 23,
      improvedLayout: true,
      hierarchical: {
        enabled: false,
      },
    },
    interaction: {
      navigationButtons: true,
      dragNodes: true,
      dragView: true,
      hideEdgesOnDrag: false,
      hideEdgesOnZoom: false,
      hideNodesOnDrag: false,
      hover: true,
      hoverConnectedEdges: true,
      keyboard: {
        enabled: false,
      },
      multiselect: false,
      selectable: true,
      selectConnectedEdges: true,
      tooltipDelay: 300,
      zoomSpeed: 1,
      zoomView: true
    },
    clickToUse: false,
    nodes: {
      shape: "box",
      borderWidth: 1,
      shapeProperties: {
        borderRadius: 1,
      },
      opacity: 1,
      fixed: false, // 允許拖曳，但初始位置由智能算法決定
      font: {
        color: "#343434",
        size: 14,
        face: "arial",
        background: "none",
        strokeWidth: 0,
        strokeColor: "#ffffff",
        align: "left",
        multi: true, // A-4: 啟用 HTML 標籤支援以控制字體大小
        vadjust: 0,
        bold: {
          color: "#343434",
          size: 14,
          face: "arial",
          vadjust: 0,
          mod: "bold",
        },
      },
      hidden: false,
      label: "HTML",
      level: undefined,
      margin: 10,
      shadow: {
        color: "rgba(33,33,33,.7)",
        size: 10,
        x: 10,
        y: 10,
      },
      heightConstraint: { minimum: 100, valign: "middle" },
      widthConstraint: { minimum: 100, maximum: 100 },
      mass: 1,
      physics: false,
    },
    groups: {
      idea: {
        color: {
          border: '#FFC',
          background: '#FFC',
          highlight: {
            border: '#FFC',
            background: '#FFC'
          }
        },
        borderWidth: 1,
        borderWidthSelected: 2
      }
    }
  };

  // 初始化 WebSocket
  useEffect(() => {
    const socket = io.connect(url.socketioHost, { path: '/s/socket.io' });
    setWs(socket);
    
    socket.on("connect", () => {
      console.log("WebSocket 已連接");
    });

    // 監聽新節點事件
    socket.on(`node-recieve-${id}`, async (body) => {
      console.log('🔔 [Teacher IdeaWall] ========== 收到新節點事件 ==========');
      console.log('  - 完整數據:', JSON.stringify(body, null, 2));
      console.log('  - 節點 ID:', body.id);
      console.log('  - 節點標題:', body.title);
      console.log('  - 節點類型:', body.node_type);
      console.log('  - 節點內容:', body.content);
      console.log('  - 作者ID:', body.author_id);
      console.log('  - 作者名稱:', body.author_name);
      console.log('  - 小組ID:', body.group_id);
      console.log('  - 課程派發ID:', body.course_dispatch_id);
      console.log('  - 牆面類型:', body.wall_type);
      console.log('  - 狀態:', body.status);
      console.log('  - 創建時間:', body.created_at);
      
      try {
        // 檢查是否為想法牆節點或老師的關鍵提問
        const isIdeaWallNode = body.wall_type === 'idea';
        // 嚴格使用 is_teacher_key_question 標記，不能用 task_id 判斷（學生的問題也有 task_id）
        const isTeacherKeyQuestion = body.is_teacher_key_question === true;
        const isQuestionWallReply = body.wall_type === 'question' && body.node_type === 'reply';
        
        // 過濾掉問題牆的學生回覆
        if (isQuestionWallReply) {
          console.log('⚠️ [Teacher IdeaWall] 過濾掉問題牆的學生回覆節點，不在想法牆顯示');
          return;
        }
        
        if (isIdeaWallNode || isTeacherKeyQuestion) {
          if (isTeacherKeyQuestion) {
            console.log('✅ [Teacher IdeaWall] 是老師的關鍵提問，準備更新');
          } else {
            console.log('✅ [Teacher IdeaWall] 是想法牆節點，準備更新');
          }
          
          // 轉換節點格式以匹配現有的處理邏輯
          const convertedNode = {
            id: body.id,
            title: body.title,
            content: body.content,
            node_type: body.node_type,
            author_name: body.author_name,
            author_id: body.author_id,
            group_id: body.group_id,
            course_dispatch_id: body.course_dispatch_id,
            wall_type: body.wall_type,
            status: body.status,
            created_at: body.created_at,
            position_x: body.position_x || Math.random() * 400 + 100,
            position_y: body.position_y || Math.random() * 300 + 100,
            size_width: body.size_width || 250,
            size_height: body.size_height || 180,
            color: body.color,
            parent_node_id: body.parent_node_id // 包含 parent_node_id
          };
          
          console.log('  - 父節點ID:', body.parent_node_id);
          
          console.log('🔄 [Teacher IdeaWall] 轉換後的節點:', convertedNode);
          
          // 更新 allNodes 狀態
          setAllNodes(prevNodes => {
            // 檢查節點是否已存在
            const exists = prevNodes.some(node => String(node.id) === String(convertedNode.id));
            if (exists) {
              console.log('⚠️ [Teacher IdeaWall] 節點已存在，跳過添加:', convertedNode.id);
              return prevNodes;
            }
            
            console.log('✅ [Teacher IdeaWall] 添加新節點到 allNodes:', convertedNode.id);
            return [...prevNodes, convertedNode];
          });
          
          // 更新 wallNodes 狀態（用於圖形顯示）
          setWallNodes(prevNodes => {
            // 檢查節點是否已存在
            const exists = prevNodes.some(node => String(node.id) === String(convertedNode.id));
            if (exists) {
              console.log('⚠️ [Teacher IdeaWall] 節點已存在於 wallNodes，跳過添加:', convertedNode.id);
              return prevNodes;
            }
            
            console.log('✅ [Teacher IdeaWall] 添加新節點到 wallNodes:', convertedNode.id);
            return [...prevNodes, convertedNode];
          });
          
          // 更新 ideas 狀態（用於圖形渲染）
          const groupId = convertedNode.group_id;
          console.log('🔄 [Teacher IdeaWall] 更新 ideas 狀態，小組ID:', groupId);
          
          // 檢查新節點所屬的小組是否在選中列表中，如果沒有則自動添加
          setSelectedGroups(prevSelected => {
            const groupIdStr = String(groupId); // 確保是字串
            console.log('🔍 [Teacher IdeaWall] 檢查選中小組列表:');
            console.log('  - 當前選中小組:', prevSelected);
            console.log('  - 新節點小組ID (原始):', groupId);
            console.log('  - 新節點小組ID (字串):', groupIdStr);
            console.log('  - 是否已選中:', prevSelected.includes(groupIdStr));
            
            if (!prevSelected.includes(groupIdStr)) {
              console.log('🔄 [Teacher IdeaWall] 新節點來自未選中的小組，自動添加到選中列表:', groupIdStr);
              const newSelected = [...prevSelected, groupIdStr];
              console.log('  - 更新後選中小組:', newSelected);
              return newSelected;
            }
            console.log('✅ [Teacher IdeaWall] 新節點小組已在選中列表中');
            return prevSelected;
          });
          
          // 轉換為 ideas 格式
          // 如果有 parent_node_id，將其添加到 relatedTo（但實際上是反向的，需要等待邊緣事件來正確設置）
          // 注意：relatedTo 應該是「這個節點指向的節點」，而不是「指向這個節點的節點」
          // 如果這是回覆節點（node_type === 'reply'），relatedTo 應該是空數組
          // 邊緣關係會通過 edge-recieve 事件來更新
          const ideaForIdeas = {
            id: convertedNode.id,
            title: convertedNode.title,
            content: convertedNode.content,
            tags: convertedNode.node_type,
            emoji: getNodeEmoji(convertedNode.node_type),
            author: convertedNode.author_name,
            time: formatTimestamp(convertedNode.created_at),
            position: {
              x: convertedNode.position_x,
              y: convertedNode.position_y
            },
            relatedTo: [] // 新節點暫時沒有連線，等待邊緣事件更新
          };
          
          console.log('  - 新節點 parent_node_id:', convertedNode.parent_node_id);
          
          console.log('🔄 [Teacher IdeaWall] 轉換為 ideas 格式:', ideaForIdeas);
          
          setIdeas(prevIdeas => {
            const groupIdeas = prevIdeas[groupId] || [];
            
            // 檢查節點是否已存在
            const exists = groupIdeas.some(idea => String(idea.id) === String(convertedNode.id));
            if (exists) {
              console.log('⚠️ [Teacher IdeaWall] 節點已存在於 ideas，跳過添加:', convertedNode.id);
              return prevIdeas;
            }
            
            console.log('✅ [Teacher IdeaWall] 添加新節點到 ideas:', convertedNode.id);
            return {
              ...prevIdeas,
              [groupId]: [...groupIdeas, ideaForIdeas]
            };
          });
          
          // 高亮新節點（4秒）- 溫和效果，固定時間避免頻繁變化
          const newNodeId = String(convertedNode.id);
          setHighlightedNodes(prev => {
            const newSet = new Set(prev);
            newSet.add(newNodeId);
            return newSet;
          });
          
          // 4秒後自動取消高亮（固定時間，避免頻繁重新渲染）
          setTimeout(() => {
            setHighlightedNodes(prev => {
              const newSet = new Set(prev);
              newSet.delete(newNodeId);
              return newSet;
            });
          }, 4000);
          
          // 只在添加新節點時強制重新渲染圖形（高亮效果變化不觸發重新渲染）
          setGraphKey(prev => prev + 1);
          
          console.log('✅ [Teacher IdeaWall] 節點已成功添加到老師的牆面，將高亮4秒');
        } else {
          console.log('ℹ️ [Teacher IdeaWall] 非想法牆節點，跳過處理');
        }
      } catch (error) {
        console.error('❌ [Teacher IdeaWall] 處理新節點時發生錯誤:', error);
      }
      
      console.log('========== 新節點事件處理結束 ==========');
    });

    // 監聽新邊緣事件
    socket.on(`edge-recieve-${id}`, (body) => {
      console.log('🔗 [Teacher IdeaWall] ========== 收到新邊緣事件 ==========');
      console.log('  - 完整數據:', JSON.stringify(body, null, 2));
      console.log('  - from_node_id:', body.from_node_id);
      console.log('  - to_node_id:', body.to_node_id);
      console.log('  - edge_type:', body.edge_type);
      console.log('  - 邊緣ID:', body.id);
      console.log('  - group_id:', body.group_id);
      
      try {
        console.log('🔍 [Teacher IdeaWall] 開始處理邊緣事件更新...');
        
        // 更新 ideas 狀態中的 relatedTo 關係
        // from_node_id 節點的 relatedTo 應該包含 to_node_id
        setIdeas(prevIdeas => {
          console.log('🔍 [Teacher IdeaWall] 進入 setIdeas 回調');
          console.log('  - prevIdeas keys:', Object.keys(prevIdeas));
          console.log('  - 尋找 from_node_id:', body.from_node_id);
          console.log('  - 添加 to_node_id:', body.to_node_id);
          
          let hasUpdate = false;
          const updatedIdeas = {};
          
          // 遍歷所有小組的想法
          Object.keys(prevIdeas).forEach(groupId => {
            const groupIdeas = prevIdeas[groupId] || [];
            console.log(`  - 小組 ${groupId} 的想法數量:`, groupIdeas.length);
            console.log(`  - 小組 ${groupId} 的節點 IDs:`, groupIdeas.map(i => i.id));
            
            const updatedGroupIdeas = groupIdeas.map(idea => {
              // 如果這個節點是 from_node_id，添加 to_node_id 到 relatedTo
              // 使用字串比較以確保類型一致
              const ideaIdStr = String(idea.id);
              const fromNodeIdStr = String(body.from_node_id);
              const toNodeIdStr = String(body.to_node_id);
              
              if (ideaIdStr === fromNodeIdStr) {
                console.log(`  ✅ [Teacher IdeaWall] 找到匹配的 from_node_id: ${idea.id}`);
                // 檢查 relatedTo 中是否已包含 to_node_id
                const relatedTo = idea.relatedTo || [];
                // 將 relatedTo 中的 ID 都轉換為字串以確保類型一致
                const relatedToStr = relatedTo.map(id => String(id));
                console.log(`  - 當前 relatedTo:`, relatedTo);
                console.log(`  - 當前 relatedTo (字串):`, relatedToStr);
                console.log(`  - 要添加的 to_node_id:`, body.to_node_id);
                console.log(`  - 要添加的 to_node_id (字串):`, toNodeIdStr);
                
                if (!relatedToStr.includes(toNodeIdStr)) {
                  console.log(`✅ [Teacher IdeaWall] 更新節點 ${idea.id} 的 relatedTo，添加 ${body.to_node_id}`);
                  hasUpdate = true;
                  // 確保添加的 ID 類型與 existing 保持一致（如果 existing 都是數字，則轉為數字；否則保持字串）
                  const toNodeId = relatedTo.length > 0 && typeof relatedTo[0] === 'number' 
                    ? Number(body.to_node_id) 
                    : body.to_node_id;
                  return {
                    ...idea,
                    relatedTo: [...relatedTo, toNodeId]
                  };
                } else {
                  console.log(`⚠️ [Teacher IdeaWall] 節點 ${idea.id} 的 relatedTo 中已包含 ${body.to_node_id}，跳過`);
                }
              }
              return idea;
            });
            
            updatedIdeas[groupId] = updatedGroupIdeas;
          });
          
          if (hasUpdate) {
            console.log('✅ [Teacher IdeaWall] 邊緣關係已更新到 ideas 狀態');
            // 強制重新渲染圖形以反映新的邊緣關係
            setTimeout(() => {
              setGraphKey(prev => prev + 1);
            }, 100);
          } else {
            console.log('⚠️ [Teacher IdeaWall] 未找到匹配的 from_node_id，可能是節點尚未載入');
            const allNodeIds = Object.values(prevIdeas).flat().map(i => i.id);
            console.log('  - 當前 ideas 中的所有節點 IDs:', allNodeIds);
            console.log('  - 是否包含 from_node_id:', allNodeIds.includes(Number(body.from_node_id)) || allNodeIds.includes(String(body.from_node_id)));
          }
          
          return updatedIdeas;
        });
      } catch (error) {
        console.error('❌ [Teacher IdeaWall] 處理邊緣事件時發生錯誤:', error);
        console.error('  - 錯誤堆疊:', error.stack);
      }
      
      console.log('========== 邊緣事件處理結束 ==========');
    });

    return () => {
      socket.off(`node-recieve-${id}`);
      socket.off(`edge-recieve-${id}`);
      socket.disconnect();
    };
  }, [id]);

  // 獲取課程派發數據（包括小組信息）
  const fetchCourseDispatchData = async () => {
    if (!id) return;
    
    // 立即設置 courseDispatchId 到 sessionStorage，供 NodeCreator 使用
    sessionStorage.setItem('courseDispatchId', String(id));
    console.log('✅ [Teacher IdeaWall] 初始化時設置 courseDispatchId 到 sessionStorage:', id);
    
    try {
      setLoading(true);
      setError(null);
      
      // 獲取課程派發詳情，包含小組信息
      const response = await axiosInstance.get(`${config.getCourseDispatchDetail}${id}/detail`);
      
      if (response.data.success) {
        const courseData = response.data.data;
        
        console.log('獲取到的課程派發數據:', courseData);
        
        // 設置課程派發資訊
        if (courseData.applicationInfo) {
          setCourseDispatch(courseData.applicationInfo);
        }
        
        // 設置小組數據 - 從正確的路徑獲取
        const fetchedGroups = courseData.taskGroupMatrix?.groups || [];
        console.log('獲取到的小組數據:', fetchedGroups);
        setGroups(fetchedGroups);
        
        // 如果有小組，預設選擇全部小組
        if (fetchedGroups.length > 0) {
          const allGroupIds = fetchedGroups.map(g => String(g.id)); // 確保是字串
          console.log('🔍 [Teacher IdeaWall] 預設選擇全部小組:', allGroupIds);
          setSelectedGroups(allGroupIds);
        }
        
        // 獲取想法牆節點
        await fetchWallNodes();
        
      } else {
        setError(response.data.message || '獲取課程數據失敗');
      }
      
    } catch (error) {
      console.error('獲取課程派發數據錯誤:', error);
      setError('獲取課程數據失敗');
    } finally {
      setLoading(false);
    }
  };

  // 獲取想法牆節點
  const fetchWallNodes = async () => {
    if (!id) return;
    
    try {
      console.log('正在獲取想法牆節點，課程派發ID:', id);
      
      // 同時獲取「想法牆」與「問題牆」節點，以及邊緣
      const [ideaNodesResp, questionNodesResp, edgesResponse] = await Promise.all([
        axiosInstance.get(`${config.getWallNodes}${id}/idea`),
        axiosInstance.get(`${config.getWallNodes}${id}/question`),
        axiosInstance.get(`${config.getNodeEdges}${id}`)
      ]);
      
      console.log('牆面節點API響應(idea):', ideaNodesResp.data);
      console.log('牆面節點API響應(question):', questionNodesResp.data);
      console.log('邊緣API響應:', edgesResponse.data);
      
      if (ideaNodesResp.data.success || questionNodesResp.data.success) {
        const ideaNodes = ideaNodesResp.data.success ? (ideaNodesResp.data.data || []) : [];
        const questionNodes = questionNodesResp.data.success ? (questionNodesResp.data.data || []) : [];
        
        // 過濾問題牆節點：只保留老師的關鍵提問，過濾掉學生的問題和回覆
        // 注意：想法牆的回覆節點（wall_type === 'idea' && node_type === 'reply'）應該保留
        const filteredQuestionNodes = questionNodes.filter(node => {
          // 過濾掉問題牆的學生回覆（問題牆的回覆不應該顯示在想法牆）
          if (node.wall_type === 'question' && node.node_type === 'reply') {
            console.log('⚠️ [老師端想法牆] 過濾掉問題牆的學生回覆節點:', {
              id: node.id,
              title: node.title,
              wall_type: node.wall_type,
              node_type: node.node_type
            });
            return false;
          }
          // 嚴格使用後端標記 is_teacher_key_question 來判斷是否是老師的關鍵提問
          // 注意：學生的問題也會有 task_id（針對關鍵提問提出），所以不能用 task_id 判斷
          // 老師的關鍵提問的 node_type 可以是任何類型，包括 'question'
          const isTeacherKeyQuestion = node.is_teacher_key_question === true;
          
          if (isTeacherKeyQuestion) {
            console.log('✅ [老師端想法牆] 保留老師的關鍵提問節點:', {
              id: node.id,
              title: node.title,
              wall_type: node.wall_type,
              node_type: node.node_type,
              task_id: node.task_id,
              is_teacher_key_question: node.is_teacher_key_question
            });
            return true;
          } else {
            console.log('⚠️ [老師端想法牆] 過濾掉問題牆的學生問題節點:', {
              id: node.id,
              title: node.title,
              wall_type: node.wall_type,
              node_type: node.node_type,
              task_id: node.task_id,
              is_teacher_key_question: node.is_teacher_key_question
            });
            return false;
          }
        });
        
        const nodes = [...ideaNodes, ...filteredQuestionNodes];
        const edges = edgesResponse.data.success ? (edgesResponse.data.data || []) : [];
        
        console.log('獲取到的牆面節點 (含問題牆):', nodes);
        console.log('  - 想法牆節點數量:', ideaNodes.length);
        console.log('  - 問題牆原始節點數量:', questionNodes.length);
        console.log('  - 過濾後問題牆節點數量（老師關鍵提問）:', filteredQuestionNodes.length);
        console.log('  - 總節點數量:', nodes.length);
        console.log('獲取到的邊緣:', edges);
        setWallNodes(nodes);
        
        // 處理邊緣關係
        const edgesMap = new Map();
        edges.forEach(edge => {
          if (edge && edge.from_node_id && edge.to_node_id) {
            if (!edgesMap.has(edge.from_node_id)) {
              edgesMap.set(edge.from_node_id, []);
            }
            edgesMap.get(edge.from_node_id).push(edge.to_node_id);
          }
        });
        
        // 將節點按小組分組
        const groupedIdeas = {};
        nodes.forEach(node => {
          const groupId = node.group_id;
          if (!groupedIdeas[groupId]) {
            groupedIdeas[groupId] = [];
          }
          
          // 轉換為前端格式，與 Forum.js 的 genNode 函數格式一致
          const emoji = getEmojiForNodeType(node.node_type);
          const author = node.author_name || '未知';
          const createdAt = node.created_at;
          
          // 獲取標題，優先使用 title，否則使用 content
          let rawTitle = node.title || node.content || '無標題';
          
          // 檢查是否包含 HTML 標籤
          const hasHTMLTags = /<[^>]+>/.test(rawTitle);
          
          // 如果包含 HTML，提取純文字
          let cleanTitle = hasHTMLTags ? extractTextFromHTML(rawTitle) : rawTitle;
          
          // 移除標題開頭的圖標，避免重複顯示
          cleanTitle = cleanTitle.replace(/^[💡📄🧪🔍❓📋💬]\s*/, '');
          
          // 限制標題長度，避免過長
          if (cleanTitle.length > 30) {
            cleanTitle = cleanTitle.substring(0, 30) + '...';
          }
          
          const timeStr = createdAt ? formatTimestamp(createdAt) : '';
          
          const ideaObj = {
            id: node.id,
            emoji: emoji,
            title: cleanTitle, // 使用清理後的標題
            author: author,
            time: timeStr, // 使用與 Forum.js 一致的時間格式
            tags: mapNodeTypeToTag(node.node_type),
            relatedTo: edgesMap.get(node.id) || [], // 添加邊緣關係
            content: node.content,
            groupId: node.group_id
          };
          
          groupedIdeas[groupId].push(ideaObj);
        });
        
        console.log('處理後的想法分組:', groupedIdeas);
        setIdeas(groupedIdeas);
      } else {
        console.log('API 返回失敗:', response.data.message);
        setIdeas({});
      }
    } catch (error) {
      console.error('獲取牆面節點錯誤:', error);
      console.error('錯誤詳情:', error.response?.data || error.message);
      // 如果沒有牆面節點 API，使用空數據
      setIdeas({});
    }
  };

  // 根據節點類型獲取 emoji
  const getEmojiForNodeType = (nodeType) => {
    const emojiMap = {
      'idea': '💡',
      'question': '❓',
      'info': '🔍',
      'experiment': '🧪',
      'record': '📄',
      'task': '📋',
      'reply': '💬'
    };
    return emojiMap[nodeType] || '💡';
  };


  // 從 HTML 中提取純文字
  const extractTextFromHTML = (html) => {
    if (!html) return '';
    
    // 創建臨時 DOM 元素來解析 HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // 獲取純文字內容
    let text = tempDiv.textContent || tempDiv.innerText || '';
    
    // 移除多餘的空白和換行
    text = text.replace(/\s+/g, ' ').trim();
    
    return text;
  };

  // 將後端節點類型映射為前端標籤
  const mapNodeTypeToTag = (nodeType) => {
    const tagMap = {
      'idea': 'idea',
      'question': 'question',
      'info': 'info',
      'experiment': 'experiment',
      'record': 'record',
      'task': 'idea',
      'reply': 'reply'
    };
    return tagMap[nodeType] || 'idea';
  };

  useEffect(() => {
    fetchCourseDispatchData();
  }, [id]);

  // A-3: 使用分層架構選單
  const allPhaseMenuGroups = createAllPhaseMenuGroups('implementation', 'idea-wall', navigate, id);


  // 刪除想法
  const deleteIdea = (ideaId, groupId) => {
    if (!window.confirm('確定要刪除這個想法嗎？')) return;
    
    setIdeas(prev => ({
      ...prev,
      [groupId]: prev[groupId].filter(idea => idea.id !== ideaId)
    }));
    
    setSnackbar({ open: true, message: '想法已刪除！', severity: 'success' });
  };

  // 查看指定組別的想法牆
  const handleEnterStudentView = (groupId) => {
    // 在導航前設置 sessionStorage，確保 NodeCreator 能立即獲取到這些值
    if (id) {
      sessionStorage.setItem('courseDispatchId', String(id));
      console.log('✅ [Teacher IdeaWall] 設置 courseDispatchId 到 sessionStorage:', id);
    }
    
    const targetGroupId = groupId || (selectedGroups.length > 0 ? selectedGroups[0] : groups[0]?.id);
    
    if (targetGroupId) {
      // 設置 groupId 和 viewingGroupId，確保老師能為該小組創建節點
      sessionStorage.setItem('groupId', String(targetGroupId));
      sessionStorage.setItem('viewingGroupId', String(targetGroupId));
      // 設置老師標記，確保 NodeCreator 知道這是老師模式
      sessionStorage.setItem('isTeacher', 'true');
      sessionStorage.setItem('userRole', 'teacher');
      console.log('✅ [Teacher IdeaWall] 設置小組ID到 sessionStorage:', {
        groupId: targetGroupId,
        courseDispatchId: id,
        isTeacher: true
      });
      
      // 進入特定小組的學生想法牆
      navigate(`/student/idea-wall/${id}/${targetGroupId}`);
    } else {
      console.warn('⚠️ [Teacher IdeaWall] 無法進入學生想法牆：找不到小組ID');
    }
  };

  // 加載狀態
  if (loading) {
    return (
      <div className="teacher-layout class-application-idea-wall-container">
        <div className="main-content-v2">
          <Navbar 
            allPhaseMenuGroups={allPhaseMenuGroups}
            currentPhase="implementation"
            currentCourseId={id}
            onPhaseChange={(phase) => {
              if (phase === 'preparation') {
                navigate('/teacher/preparation');
              } else if (phase === 'implementation') {
                navigate('/teacher/implementation');
              } else if (phase === 'diagnosis') {
                navigate('/teacher/diagnosis');
              }
            }}
            onCourseChange={(newCourseId) => {
              const currentPath = window.location.pathname;
              const newPath = currentPath.replace(/\/\d+(\/|$)/, `/${newCourseId}$1`);
              navigate(newPath);
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ ml: 2 }}>載入想法牆資料中...</Typography>
          </Box>
        </div>
      </div>
    );
  }

  // 錯誤狀態
  if (error) {
    return (
      <div className="teacher-layout class-application-idea-wall-container">
        <div className="main-content-v2">
          <Navbar 
            allPhaseMenuGroups={allPhaseMenuGroups}
            currentPhase="implementation"
            currentCourseId={id}
            onPhaseChange={(phase) => {
              if (phase === 'preparation') {
                navigate('/teacher/preparation');
              } else if (phase === 'implementation') {
                navigate('/teacher/implementation');
              } else if (phase === 'diagnosis') {
                navigate('/teacher/diagnosis');
              }
            }}
            onCourseChange={(newCourseId) => {
              const currentPath = window.location.pathname;
              const newPath = currentPath.replace(/\/\d+(\/|$)/, `/${newCourseId}$1`);
              navigate(newPath);
            }}
          />
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
            <Button onClick={fetchCourseDispatchData} sx={{ ml: 2 }}>
              重新載入
            </Button>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="teacher-layout class-application-idea-wall-container">
      <div className="main-content-v2">
        <Navbar 
          allPhaseMenuGroups={allPhaseMenuGroups}
          currentPhase="implementation"
          currentCourseId={id}
          onPhaseChange={(phase) => {
            if (phase === 'preparation') {
              navigate('/teacher/preparation');
            } else if (phase === 'implementation') {
              navigate('/teacher/implementation');
            } else if (phase === 'diagnosis') {
              navigate('/teacher/diagnosis');
            }
          }}
          onCourseChange={(newCourseId) => {
            const currentPath = window.location.pathname;
            const newPath = currentPath.replace(/\/\d+(\/|$)/, `/${newCourseId}$1`);
            navigate(newPath);
          }}
        />
        
        <div className="class-header-row">
          <h1 className="main-title">
            想法牆
            {courseDispatch && (
              <Typography variant="subtitle1" color="text.secondary" sx={{ ml: 1, display: 'inline' }}>
                {courseDispatch.title || courseDispatch.activity_name || courseDispatch.lessonPackage || '課程'}
              </Typography>
            )}
          </h1>
        </div>

        {/* 小組選擇和觀察模式 */}
        <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#1e4d5f', fontWeight: 'bold', textAlign: 'center' }}>
            🎯 選擇觀察小組
          </Typography>
          
          {/* 觀察模式切換 */}
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={viewMode === 'multiple'}
                  onChange={(e) => setViewMode(e.target.checked ? 'multiple' : 'single')}
                />
              }
              label={viewMode === 'multiple' ? '多組觀察模式' : '單組觀察模式'}
            />
          </Box>
          
          {/* 小組選擇 */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, justifyContent: 'center' }}>
            {[...groups].sort((a, b) => {
              // 按組別ID排序（確保組別順序一致）
              const idA = parseInt(a.id) || 0;
              const idB = parseInt(b.id) || 0;
              if (idA !== idB) return idA - idB;
              // 如果ID相同，按名稱排序
              const nameA = a.name || a.groupName || '';
              const nameB = b.name || b.groupName || '';
              return nameA.localeCompare(nameB, 'zh-TW');
            }).map(group => (
              <Chip
                key={group.id}
                label={`${group.name} (${ideas[group.id]?.length || 0} 個想法)`}
                onClick={() => handleGroupToggle(group.id)}
                color={selectedGroups.includes(group.id) ? 'primary' : 'default'}
                variant={selectedGroups.includes(group.id) ? 'filled' : 'outlined'}
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: selectedGroups.includes(group.id) ? '#1e4d5f' : '#f0f8ff'
                  }
                }}
              />
            ))}
          </Box>
          
          {/* 選中組統計 */}
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            已選擇 {selectedGroups.length} 個小組，共 {getAllSelectedIdeas().length} 個想法
          </Typography>
        </Box>

        {/* 想法牆顯示區域 - Forumm 預覽方式 */}
        <Box sx={{ height: 'calc(100vh - 300px)', overflow: 'auto' }}>
          {viewMode === 'multiple' ? (
            // 多組觀察模式 - 兩兩排列
            <Grid container spacing={2}>
              {[...selectedGroups].sort((a, b) => {
                // 按組別ID排序（確保組別順序一致）
                const idA = parseInt(a) || 0;
                const idB = parseInt(b) || 0;
                if (idA !== idB) return idA - idB;
                // 如果ID相同，按組別名稱排序
                const groupA = groups.find(g => g.id === a);
                const groupB = groups.find(g => g.id === b);
                const nameA = groupA?.name || groupA?.groupName || '';
                const nameB = groupB?.name || groupB?.groupName || '';
                return nameA.localeCompare(nameB, 'zh-TW');
              }).map(groupId => {
                const group = groups.find(g => g.id === groupId);
                const groupIdeas = ideas[groupId] || [];
                const groupGraph = convertIdeasToGraph(groupIdeas);
                
                return (
                  <Grid item xs={12} md={6} key={groupId}>
                    <Card sx={{ border: '2px solid #1e4d5f', height: '100%' }}>
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6" sx={{ color: '#1e4d5f', fontWeight: 'bold' }}>
                            {group?.name} 想法牆
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#1e4d5f' }}>
                              總想法: {groupIdeas.length}
                            </Typography>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                              onClick={() => handleEnterStudentView(groupId)}
                              sx={{
                                borderColor: '#2b687f',
                                color: '#2b687f',
                                fontSize: '0.7rem',
                                textTransform: 'none',
                                py: 0.3,
                                px: 1,
                                minHeight: '28px',
                                borderRadius: '4px',
                                '&:hover': {
                                  backgroundColor: '#e8f4f8',
                                  borderColor: '#1e4d5f',
                                  color: '#1e4d5f'
                                }
                              }}
                            >
                              查看
                            </Button>
                          </Box>
                        </Box>
                        
                        <Box sx={{ 
                          height: '350px', 
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          bgcolor: '#fafafa'
                        }}>
                          <Graph 
                            key={`${graphKey}-${groupId}`}
                            graph={groupGraph} 
                            options={{
                              ...options,
                              interaction: {
                                ...options.interaction,
                                navigationButtons: false
                              }
                            }}
                            events={events}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            // 單組觀察模式 - vis-network 圖形
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  {groups.find(g => g.id === selectedGroups[0])?.name} 想法牆
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                    onClick={() => handleEnterStudentView(selectedGroups[0])}
                    sx={{
                      borderColor: '#2b687f',
                      color: '#2b687f',
                      fontSize: '0.7rem',
                      textTransform: 'none',
                      py: 0.3,
                      px: 1,
                      minHeight: '28px',
                      borderRadius: '4px',
                      '&:hover': {
                        backgroundColor: '#e8f4f8',
                        borderColor: '#1e4d5f',
                        color: '#1e4d5f'
                      }
                    }}
                  >
                    查看
                  </Button>
                </Box>
              </Box>
              
              <Card sx={{ height: 'calc(100vh - 350px)', border: '2px solid #1e4d5f' }}>
                <CardContent sx={{ height: '100%', p: 0 }}>
                  <Box sx={{ 
                    height: '100%',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}>
                    <Graph key={graphKey} graph={graph} options={options} events={events} />
                  </Box>
                </CardContent>
              </Card>
              
              {/* 統計資訊 */}
              <Box sx={{ 
                mt: 2,
                display: 'flex',
                justifyContent: 'space-around',
                bgcolor: '#f8f9fa',
                p: 2,
                borderRadius: '8px',
                border: '1px solid #e0e0e0'
              }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                    {currentIdeas.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    總想法數
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="secondary.main" sx={{ fontWeight: 'bold' }}>
                    {currentIdeas.filter(i => i.relatedTo && i.relatedTo.length > 0).length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    有連線
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="info.main" sx={{ fontWeight: 'bold' }}>
                    {currentIdeas.reduce((acc, i) => acc + (i.relatedTo ? i.relatedTo.length : 0), 0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    總連線數
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Box>

        {/* 通知訊息 */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
}

